import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, NEVER } from 'rxjs';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';
import { takeUntil, tap, catchError, finalize } from 'rxjs/operators';
import { ConnectionStatus, ConnectionEvent, WebSocketMessage } from '../models';

/**
 * Pure WebSocket connection management service
 * Handles only the raw WebSocket connection, sending, and receiving
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketConnectionService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly statusSubject$ = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  private readonly messagesSubject$ = new Subject<WebSocketMessage>();
  private readonly eventsSubject$ = new Subject<ConnectionEvent>();

  private websocket$: WebSocketSubject<any> | null = null;
  private currentUrl: string | null = null;
  private connectionTimeout: number = 10000;
  private connectionStartTime: number = 0;
  private lastActivityTime: number = Date.now();
  private connectionAttempts: number = 0;
  private failureHistory: Array<{ timestamp: number; error: string; code?: number }> = [];

  // CONNECTION GUARD: Prevent multiple simultaneous connections
  private isConnectionInProgress = false;
  private connectionGuardTimeout: number | null = null;
  private lastConnectionAttemptTime: number = 0;
  private readonly minimumConnectionInterval = 1000; // 1 second between connection attempts

  // Enhanced connection health tracking
  private heartbeatReceived: boolean = false;
  private connectionHealthScore: number = 100;

  // PING SYSTEM: Immediate connection verification
  private immediatePingTimer: number | null = null;
  private connectionVerificationTimeout: number | null = null;

  // Enhanced ping tracking
  private activePings: Map<string, { timestamp: number; timeout: number }> = new Map();
  private pingResponseRate: number = 100; // Percentage of pings that get responses
  private lastPingResponseTime: number = 0;
  private consecutiveFailedPings: number = 0;
  private readonly MAX_CONSECUTIVE_PING_FAILURES = 4; // Max 4 failed pings before triggering reconnection

  // CIRCUIT BREAKER IMPLEMENTATION
  private emergencyStopActivated: boolean = false;
  private emergencyStopTime: number = 0;
  private readonly EMERGENCY_COOLDOWN_PERIOD = 30000; // 30 seconds mandatory cooldown
  private circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private circuitOpenTime: number = 0;
  private readonly CIRCUIT_RECOVERY_TIMEOUT = 60000; // 60 seconds before HALF_OPEN

  // Public observables
  readonly status$ = this.statusSubject$.asObservable();
  readonly messages$ = this.messagesSubject$.asObservable();
  readonly events$ = this.eventsSubject$.asObservable();

  connect(url: string, timeout: number = 10000): Observable<boolean> {
    const currentTime = Date.now();

    // CONNECTION GUARD: Prevent multiple simultaneous connections
    if (this.isConnectionInProgress) {
      console.warn('🚫 CONNECTION GUARD: Connection already in progress, rejecting new attempt:', {
        timestamp: new Date(currentTime).toISOString(),
        currentUrl: this.currentUrl,
        newUrl: url,
        timeSinceLastAttempt: currentTime - this.lastConnectionAttemptTime,
        currentStatus: this.getStatus()
      });

      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    // CONNECTION GUARD: Enforce minimum interval between attempts
    const timeSinceLastAttempt = currentTime - this.lastConnectionAttemptTime;
    if (timeSinceLastAttempt < this.minimumConnectionInterval) {
      console.warn('🚫 CONNECTION GUARD: Too soon since last attempt, rejecting:', {
        timestamp: new Date(currentTime).toISOString(),
        timeSinceLastAttempt: timeSinceLastAttempt,
        minimumInterval: this.minimumConnectionInterval,
        url: url
      });

      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    // CONNECTION GUARD: Force cleanup of any existing connection
    if (this.websocket$ || this.getStatus() !== ConnectionStatus.DISCONNECTED) {
      console.log('🧹 CONNECTION GUARD: Cleaning up existing connection before new attempt:', {
        timestamp: new Date(currentTime).toISOString(),
        hasWebSocket: !!this.websocket$,
        currentStatus: this.getStatus(),
        newUrl: url
      });
      this.forceDisconnect();
    }

    // Set connection guard
    this.isConnectionInProgress = true;
    this.lastConnectionAttemptTime = currentTime;
    this.currentUrl = url;
    this.connectionTimeout = timeout;
    this.connectionStartTime = currentTime;
    this.connectionAttempts++;

    // Set guard timeout to prevent permanent lock
    this.connectionGuardTimeout = window.setTimeout(() => {
      if (this.isConnectionInProgress) {
        console.warn('⚠️ CONNECTION GUARD: Timeout reached, releasing connection guard');
        this.releaseConnectionGuard();
      }
    }, Math.max(timeout * 1.5, 15000)); // 1.5x timeout or 15 seconds minimum

    console.log('🔌 CONNECTION GUARD: Starting new connection attempt:', {
      timestamp: new Date(currentTime).toISOString(),
      url: url,
      attempt: this.connectionAttempts,
      timeout: timeout,
      guardTimeout: Math.max(timeout * 1.5, 15000)
    });

    return new Observable(observer => {
      this.statusSubject$.next(ConnectionStatus.CONNECTING);
      this.emitEvent('connecting', { url, attempt: this.connectionAttempts });

      const config: WebSocketSubjectConfig<any> = {
        url,
        openObserver: {
          next: () => {
            const connectionTime = Date.now() - this.connectionStartTime;
            this.statusSubject$.next(ConnectionStatus.CONNECTED);
            this.lastActivityTime = Date.now();
            this.connectionHealthScore = 100;

            // Track connection attempts for reconnection detection
            const wasReconnection = this.connectionAttempts > 1;

            console.log('✅ CONNECTION GUARD: Connection established successfully:', {
              timestamp: new Date().toISOString(),
              url: url,
              connectionTime: connectionTime,
              attempt: this.connectionAttempts,
              wasReconnection: wasReconnection
            });

            this.emitEvent('connected', {
              url,
              connectionTime,
              attempt: this.connectionAttempts,
              wasReconnection: wasReconnection,
              totalAttempts: this.connectionAttempts
            });

            // Release connection guard on success
            this.releaseConnectionGuard();

            // FIX: Reset circuit breaker and emergency state immediately on successful connection
            this.resetCircuitBreakerOnConnection();

            // Reset attempts after successful connection
            this.connectionAttempts = 0;

            // IMMEDIATE PING: Verify connection works immediately
            this.scheduleImmediateConnectionVerification();

            observer.next(true);
            observer.complete();
          }
        },
        closeObserver: {
          next: (event) => {
            const disconnectionInfo = {
              url,
              code: event.code,
              reason: event.reason,
              wasAbnormalClosure: event.code === 1006,
              wasClean: event.wasClean,
              connectionDuration: this.lastActivityTime ? Date.now() - this.lastActivityTime : 0,
              connectionHealthScore: this.connectionHealthScore
            };

            console.log('🔌 CONNECTION GUARD: Connection closed:', {
              timestamp: new Date().toISOString(),
              url: url,
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean
            });

            this.recordFailure(event.code, event.reason);
            this.statusSubject$.next(ConnectionStatus.DISCONNECTED);
            this.emitEvent('disconnected', disconnectionInfo);

            // MEMORY LEAK PREVENTION: Clean up all timers on disconnect
            this.releaseConnectionGuard();
            this.clearConnectionVerificationTimers();
            this.websocket$ = null;
          }
        }
      };

      try {
        this.websocket$ = webSocket(config);

        // Enhanced message handling with health monitoring
        this.websocket$
          .pipe(
            takeUntil(this.destroy$),
            tap(message => {
              this.lastActivityTime = Date.now();

              // Track heartbeat and ping responses for connection health
              if (message.event_type === 'pong') {
                this.heartbeatReceived = true;
                this.lastPingResponseTime = Date.now();
                this.updateConnectionHealth(10); // Boost health on heartbeat

                /*console.log('✅ PONG: Received pong response:', {
                  timestamp: new Date().toISOString(),
                  connectionId: message.connection_id,
                  url: this.currentUrl
                });*/

                // PING SYSTEM: Check if this is a response to immediate verification
                if (message.connection_id && message.connection_id.startsWith('verify_')) {
                  console.log('✅ PING: Immediate connection verification successful:', {
                    timestamp: new Date().toISOString(),
                    connectionId: message.connection_id,
                    url: this.currentUrl
                  });

                  // Clear verification timeout - connection is verified
                  if (this.connectionVerificationTimeout) {
                    window.clearTimeout(this.connectionVerificationTimeout);
                    this.connectionVerificationTimeout = null;
                  }

                  this.emitEvent('verification_success', {
                    url: this.currentUrl,
                    connectionId: message.connection_id
                  });
                }

                // Enhanced ping tracking: Mark ping as responded
                // Match by connection ID or partial match for tracking
                this.handlePingResponse(message.connection_id);

                // Reset consecutive failed pings on successful response
                this.consecutiveFailedPings = 0;

                // CIRCUIT BREAKER: Record success and potentially close circuit
                this.recordCircuitBreakerSuccess();
              }

              // CRITICAL FIX: Accept both event_type and type fields
              // Backend sends different formats:
              // - Normal messages: {"event_type":"heartbeat",...}
              // - Session invalidated: {"type":"session_invalidated",...}
              this.messagesSubject$.next({
                data: message,
                timestamp: Date.now(),
                type: message.event_type || message.type || 'unknown'
              });
            }),
            catchError(error => {
              // FIX: Type-safe error handling for event.data.error
              let errorMessage: string;
              if (error && typeof error === 'object') {
                // Handle error object with nested error property
                const errorData = (error as any).data?.error;
                if (typeof errorData === 'string') {
                  errorMessage = errorData;
                } else if (errorData && typeof errorData === 'object') {
                  errorMessage = String(errorData);
                } else if (error instanceof Error) {
                  errorMessage = error.message;
                } else {
                  errorMessage = String(error);
                }
              } else if (typeof error === 'string') {
                errorMessage = error;
              } else {
                errorMessage = 'Unknown connection error';
              }

              const errorInfo = {
                error,
                url,
                isNetworkError: this.isNetworkRelatedError(error),
                connectionDuration: Date.now() - this.connectionStartTime,
                connectionAttempts: this.connectionAttempts,
                recentFailures: this.getRecentFailureCount()
              };

              this.recordFailure(null, errorMessage);
              this.statusSubject$.next(ConnectionStatus.FAILED);
              this.emitEvent('error', errorInfo);

              // CIRCUIT BREAKER: Record failure on connection errors
              this.recordCircuitBreakerFailure();

              // Release connection guard on error
              this.releaseConnectionGuard();

              observer.error(error);
              return NEVER;
            }),
            finalize(() => {
              if (this.statusSubject$.value === ConnectionStatus.CONNECTING) {
                this.statusSubject$.next(ConnectionStatus.DISCONNECTED);
                this.releaseConnectionGuard();
                observer.next(false);
                observer.complete();
              }
            })
          )
          .subscribe();

        // Enhanced timeout with connection health consideration
        const adaptiveTimeout = this.calculateAdaptiveTimeout(timeout);
        const timeoutId = setTimeout(() => {
          if (this.statusSubject$.value === ConnectionStatus.CONNECTING) {
            const timeoutError = new Error(`Connection timeout after ${adaptiveTimeout}ms`);
            this.recordFailure(null, 'timeout');

            console.error('⏰ CONNECTION GUARD: Connection timeout:', {
              timestamp: new Date().toISOString(),
              url: url,
              timeout: adaptiveTimeout,
              attempt: this.connectionAttempts
            });

            this.emitEvent('timeout', {
              url,
              timeout: adaptiveTimeout,
              suggestedRetryDelay: this.getSuggestedRetryDelay(),
              connectionAttempts: this.connectionAttempts,
              isLikelyNetworkIssue: this.isLikelyNetworkIssue()
            });

            // Release connection guard on timeout
            this.releaseConnectionGuard();

            observer.error(timeoutError);
          }
        }, adaptiveTimeout);

        // Cleanup timeout on successful connection or error
        const subscription = this.status$.subscribe(status => {
          if (status === ConnectionStatus.CONNECTED || status === ConnectionStatus.FAILED) {
            clearTimeout(timeoutId);
            subscription.unsubscribe();
          }
        });

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.recordFailure(null, errorMessage);
        this.statusSubject$.next(ConnectionStatus.FAILED);

        console.error('❌ CONNECTION GUARD: Connection creation error:', {
          timestamp: new Date().toISOString(),
          url: url,
          error: errorMessage
        });

        this.emitEvent('error', {
          error,
          url,
          isNetworkError: this.isNetworkRelatedError(error),
          connectionAttempts: this.connectionAttempts
        });

        // Release connection guard on creation error
        this.releaseConnectionGuard();

        observer.error(error);
      }
    });
  }

  disconnect(): void {
    console.log('🔌 CONNECTION GUARD: Manual disconnect requested:', {
      timestamp: new Date().toISOString(),
      url: this.currentUrl,
      hasWebSocket: !!this.websocket$,
      currentStatus: this.getStatus(),
      isConnectionInProgress: this.isConnectionInProgress
    });

    this.forceDisconnect();
  }

  /**
   * Force disconnect without any checks - cleans up everything
   */
  private forceDisconnect(): void {
    if (this.websocket$) {
      this.websocket$.complete();
      this.websocket$ = null;
    }

    this.statusSubject$.next(ConnectionStatus.DISCONNECTED);
    this.emitEvent('manual_disconnect', { url: this.currentUrl });

    // MEMORY LEAK PREVENTION: Clean up all timers and guards
    this.releaseConnectionGuard();
    this.clearConnectionVerificationTimers();
  }

  /**
   * Release the connection guard and cleanup
   */
  private releaseConnectionGuard(): void {
    if (this.isConnectionInProgress) {
      console.log('🔓 CONNECTION GUARD: Releasing connection guard:', {
        timestamp: new Date().toISOString(),
        url: this.currentUrl,
        wasInProgress: this.isConnectionInProgress
      });
    }

    this.isConnectionInProgress = false;

    if (this.connectionGuardTimeout) {
      window.clearTimeout(this.connectionGuardTimeout);
      this.connectionGuardTimeout = null;
    }
  }

  /**
   * PING SYSTEM: Schedule immediate connection verification
   */
  private scheduleImmediateConnectionVerification(): void {
    // Clear any existing verification timer
    this.clearConnectionVerificationTimers();

    console.log('🏓 PING: Scheduling immediate connection verification:', {
      timestamp: new Date().toISOString(),
      url: this.currentUrl,
      circuitState: this.circuitBreakerState,
      emergencyStop: this.emergencyStopActivated
    });

    // FIX: Reset circuit breaker state on successful connection
    this.resetCircuitBreakerOnConnection();

    // Schedule immediate ping after a longer delay to ensure connection and circuit breaker stability
    this.immediatePingTimer = window.setTimeout(() => {
      this.sendImmediateConnectionPing();
    }, 500); // INCREASED from 200ms to 500ms for better stability
  }

  /**
   * PING SYSTEM: Send immediate connection verification ping
   */
  private sendImmediateConnectionPing(): void {
    if (!this.isConnected()) {
      console.warn('⚠️ PING: Cannot send immediate ping - not connected');
      return;
    }

    // FIX: Double-check circuit breaker state before sending
    if (!this.canExecuteOperation()) {
      console.warn('⚠️ PING: Circuit breaker blocking immediate verification - resetting for connection health');
      this.resetCircuitBreakerOnConnection();

      // Retry once after reset
      if (!this.canExecuteOperation()) {
        console.error('❌ PING: Circuit breaker still blocking after reset');
        return;
      }
    }

    const pingMessage = {
      event_type: 'ping',
      timestamp: new Date().toISOString(),
      connection_id: this.generateConnectionVerificationId(),
      immediate_verification: true
    };

    console.log('🏓 PING: Sending immediate connection verification:', {
      timestamp: new Date().toISOString(),
      url: this.currentUrl,
      connectionId: pingMessage.connection_id,
      circuitState: this.circuitBreakerState,
      canExecute: this.canExecuteOperation()
    });

    const success = this.send(pingMessage);

    if (success) {
      // Set timeout to verify we get a response (reduced from 5s to 3s for faster detection)
      this.connectionVerificationTimeout = window.setTimeout(() => {
        console.warn('⚠️ PING: No immediate ping response received within 3 seconds');
        this.handleConnectionVerificationTimeout();
      }, 3000); // 3 second timeout for verification (Phase 1 optimization)
    } else {
      console.error('❌ PING: Failed to send immediate verification ping');

      // FIX: If send fails immediately after connection, treat as circuit breaker issue
      console.log('🔧 PING: Attempting circuit breaker reset due to send failure');
      this.resetCircuitBreakerOnConnection();

      // Try once more
      setTimeout(() => {
        if (this.isConnected() && this.canExecuteOperation()) {
          console.log('🔄 PING: Retrying immediate verification after circuit breaker reset');
          const retrySuccess = this.send(pingMessage);
          if (retrySuccess) {
            this.connectionVerificationTimeout = window.setTimeout(() => {
              console.warn('⚠️ PING: No immediate ping response received within 3 seconds (retry)');
              this.handleConnectionVerificationTimeout();
            }, 3000);
          }
        }
      }, 100); // 100ms delay for retry
    }
  }

  /**
   * PING SYSTEM: Handle connection verification timeout
   */
  private handleConnectionVerificationTimeout(): void {
    console.warn('⚠️ PING: Connection verification timeout - connection may not be fully functional');

    this.emitEvent('verification_timeout', {
      url: this.currentUrl,
      message: 'No ping response received within 3 seconds'  // Updated timeout message
    });

    // Update connection health score
    this.updateConnectionHealth(-20);
  }

  /**
   * PING SYSTEM: Generate unique connection verification ID
   */
  private generateConnectionVerificationId(): string {
    return `verify_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * PING SYSTEM: Clear all connection verification timers
   */
  private clearConnectionVerificationTimers(): void {
    if (this.immediatePingTimer) {
      window.clearTimeout(this.immediatePingTimer);
      this.immediatePingTimer = null;
    }

    if (this.connectionVerificationTimeout) {
      window.clearTimeout(this.connectionVerificationTimeout);
      this.connectionVerificationTimeout = null;
    }
  }

  send(data: any): boolean {
    // FIX: Allow immediate verification pings even if circuit breaker is temporarily open
    const isImmediateVerification = data.event_type === 'ping' && data.immediate_verification === true;

    // CIRCUIT BREAKER: Check if we can execute operations (with exception for immediate verification)
    if (!isImmediateVerification && !this.canExecuteOperation()) {
      console.warn('🚫 CIRCUIT BREAKER: Operation blocked - circuit is OPEN', {
        state: this.circuitBreakerState,
        cooldownRemaining: this.getRemainingCooldownTime(),
        operationType: data.event_type
      });
      return false;
    }

    // Special handling for immediate verification pings
    if (isImmediateVerification) {
      console.log('🔧 PING: Immediate verification ping - bypassing circuit breaker if needed', {
        circuitState: this.circuitBreakerState,
        emergencyStop: this.emergencyStopActivated,
        canExecute: this.canExecuteOperation()
      });
    }

    if (this.websocket$ && this.statusSubject$.value === ConnectionStatus.CONNECTED) {
      try {
        // Enhanced ping tracking
        if (data.event_type === 'ping' && data.connection_id) {
          this.trackPingRequest(data.connection_id);
        }

        this.websocket$.next(data);
        this.emitEvent('message_sent', { data });
        return true;
      } catch (error) {
        this.emitEvent('send_error', { error, data });

        // CIRCUIT BREAKER: Record failure on send errors (except for immediate verification)
        if (!isImmediateVerification) {
          this.recordCircuitBreakerFailure();
        }

        return false;
      }
    }
    return false;
  }

  isConnected(): boolean {
    return this.statusSubject$.value === ConnectionStatus.CONNECTED;
  }

  isConnecting(): boolean {
    return this.statusSubject$.value === ConnectionStatus.CONNECTING;
  }

  /**
   * Check if a connection attempt is currently in progress
   */
  isConnectionInProgressCheck(): boolean {
    return this.isConnectionInProgress;
  }

  getCurrentUrl(): string | null {
    return this.currentUrl;
  }

  getStatus(): ConnectionStatus {
    return this.statusSubject$.value;
  }

  private emitEvent(type: string, data: any): void {
    // Map internal event types to ConnectionEvent types
    const eventType = this.mapEventType(type);
    this.eventsSubject$.next({
      type: eventType,
      timestamp: Date.now(),
      data
    });
  }

  private mapEventType(type: string): 'status' | 'message' | 'error' | 'quality' {
    switch (type) {
      case 'connecting':
      case 'connected':
      case 'disconnected':
      case 'manual_disconnect':
        return 'status';
      case 'message_sent':
        return 'message';
      case 'error':
      case 'send_error':
      case 'timeout':
        return 'error';
      default:
        return 'message';
    }
  }

  /**
   * Check if error is network-related (hibernation, wifi changes, etc.)
   */
  private isNetworkRelatedError(error: unknown): boolean {
    if (!error) return false;

    // Handle Error objects
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      const errorName = error.name.toLowerCase();

      return errorMessage.includes('network') ||
             errorMessage.includes('timeout') ||
             errorMessage.includes('connection') ||
             errorMessage.includes('offline') ||
             errorName.includes('network');
    }

    // Handle objects with error codes
    if (typeof error === 'object' && error !== null) {
      const errorObj = error as any;
      return errorObj.code === 1006 || // Abnormal closure
             errorObj.code === 1001 || // Going away
             errorObj.code === 1012;   // Service restart
    }

    // Handle string errors
    if (typeof error === 'string') {
      const errorMessage = error.toLowerCase();
      return errorMessage.includes('network') ||
             errorMessage.includes('timeout') ||
             errorMessage.includes('connection') ||
             errorMessage.includes('offline');
    }

    return false;
  }

  /**
   * Record connection failure with timestamp and details
   */
  private recordFailure(code: number | null, reason: string): void {
    this.failureHistory.push({
      timestamp: Date.now(),
      error: reason,
      code: code || undefined
    });

    // Keep only last 20 failures to prevent memory bloat
    if (this.failureHistory.length > 20) {
      this.failureHistory = this.failureHistory.slice(-20);
    }

    // Reduce connection health score
    this.updateConnectionHealth(-15);
  }

  /**
   * Update connection health score
   */
  private updateConnectionHealth(delta: number): void {
    this.connectionHealthScore = Math.max(0, Math.min(100, this.connectionHealthScore + delta));
  }

  /**
   * Check if current issues are likely network-related
   */
  private isLikelyNetworkIssue(): boolean {
    const recentFailures = this.getRecentFailures();
    const networkErrors = recentFailures.filter(f => {
      // Safe string checking - ensure f.error is a string before calling includes
      const errorString = f.error ? String(f.error) : '';
      return errorString.includes('timeout') ||
             errorString.includes('network') ||
             f.code === 1006;
    });

    return networkErrors.length >= Math.ceil(recentFailures.length * 0.6);
  }

  /**
   * Get recent failures (last 2 minutes)
   */
  private getRecentFailures(): Array<{ timestamp: number; error: string; code?: number }> {
    const twoMinutesAgo = Date.now() - (2 * 60 * 1000);
    return this.failureHistory.filter(f => f.timestamp > twoMinutesAgo);
  }

  /**
   * Calculate adaptive timeout based on network conditions and previous failures
   */
  private calculateAdaptiveTimeout(baseTimeout: number): number {
    // Check if we're likely coming out of hibernation or network change
    const timeSinceLastConnection = this.getTimeSinceLastConnection();

    // If it's been more than 30 seconds since last activity,
    // we might be waking from hibernation - use longer timeout
    if (timeSinceLastConnection > 30000) {
      return Math.min(baseTimeout * 3, 30000); // Max 30 seconds
    }

    // For frequent reconnections (network issues), use progressive timeout
    const recentFailures = this.getRecentFailureCount();
    if (recentFailures > 2) {
      return Math.min(baseTimeout * (1 + recentFailures * 0.5), 25000);
    }

    return baseTimeout;
  }

  /**
   * Get suggested retry delay based on failure pattern
   */
  private getSuggestedRetryDelay(): number {
    const timeSinceLastConnection = this.getTimeSinceLastConnection();

    // Long gap suggests hibernation - wait for network to stabilize
    if (timeSinceLastConnection > 30000) {
      return 5000; // 5 seconds for hibernation recovery
    }

    // Rapid failures suggest ongoing network issues
    const recentFailures = this.getRecentFailureCount();
    if (recentFailures > 3) {
      return 15000; // 15 seconds for persistent network issues
    }

    return 2000; // Standard 2 second retry
  }

  /**
   * Get time since last successful connection or activity
   */
  private getTimeSinceLastConnection(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Count recent connection failures (last 2 minutes)
   */
  private getRecentFailureCount(): number {
    return this.getRecentFailures().length;
  }

  /**
   * Enhanced ping tracking: Track outgoing ping requests
   */
  private trackPingRequest(connectionId: string): void {
    const now = Date.now();
    // EMERGENCY FIX: Reduced to 5 seconds for realistic network conditions
    const pingTimeout = window.setTimeout(() => {
      this.handlePingTimeout(connectionId);
    }, 5000); // 5 second timeout for ping response (emergency fix from 10s)

    this.activePings.set(connectionId, {
      timestamp: now,
      timeout: pingTimeout
    });

    /*console.log('🏓 PING: Tracking ping request', {
      connectionId,
      timestamp: new Date().toISOString(),
      activePings: this.activePings.size,
      timeout: '5s', // DIAGNOSTIC: Changed from '10s' to reflect actual timeout
      actualTimeoutMs: 5000 // DIAGNOSTIC: Add actual timeout value
    });*/

    // Clean up old pings (older than 30 seconds - reduced from 45s)
    this.cleanupOldPings();
  }

  /**
   * Handle ping response - mark ping as successful
   */
  private handlePingResponse(connectionId: string): void {
    const pingData = this.activePings.get(connectionId);

    if (pingData) {
      // Exact match found
      this.processPingSuccess(connectionId, pingData);
    } else {
      // Try partial matching for connection IDs that might have slight variations
      const matchingPingId = this.findMatchingPingId(connectionId);
      if (matchingPingId) {
        const matchingPingData = this.activePings.get(matchingPingId);
        if (matchingPingData) {
          this.processPingSuccess(matchingPingId, matchingPingData);
        }
      } else {
        // No matching ping found - this might be a server-initiated pong
        console.log('ℹ️ PONG: Received pong without matching ping (server-initiated)', {
          connectionId,
          activePings: Array.from(this.activePings.keys()),
          timestamp: new Date().toISOString()
        });

        // Still count as positive health indicator
        this.updatePingResponseRate(true);
        this.consecutiveFailedPings = 0; // Reset on any pong response
      }
    }
  }

  /**
   * Process a successful ping response
   */
  private processPingSuccess(connectionId: string, pingData: { timestamp: number; timeout: number }): void {
    // Clear the timeout
    window.clearTimeout(pingData.timeout);
    this.activePings.delete(connectionId);

    // Calculate response time
    const responseTime = Date.now() - pingData.timestamp;

    // Reset consecutive failed pings on successful response
    this.consecutiveFailedPings = 0;

    console.log('✅ PING: Response received', {
      connectionId,
      responseTime: `${responseTime}ms`,
      timestamp: new Date().toISOString(),
      activePings: this.activePings.size,
      consecutiveFailures: this.consecutiveFailedPings
    });

    // Update ping response rate
    this.updatePingResponseRate(true);

    // Emit success event
    this.emitEvent('ping_response', {
      connectionId,
      responseTime,
      success: true,
      consecutiveFailures: this.consecutiveFailedPings
    });
  }

  /**
   * Find matching ping ID for partial connection ID matches
   */
  private findMatchingPingId(pongConnectionId: string): string | null {
    // Strategy 1: Extract timestamp prefix for matching (e.g., "conn_1756519432779_" from "conn_1756519432779_d5jdqj4vqj")
    const pongPrefix = pongConnectionId.match(/^conn_\d+_/)?.[0];

    if (pongPrefix) {
      for (const pingId of this.activePings.keys()) {
        if (pingId.startsWith(pongPrefix)) {
          console.log('🔍 PING: Found prefix match', {
            pongId: pongConnectionId,
            pingId: pingId,
            prefix: pongPrefix
          });
          return pingId;
        }
      }
    }

    // Strategy 2: Try fuzzy matching if prefix fails
    const activePingIds = Array.from(this.activePings.keys());

    // Check if any active ping ID is a substring of the pong ID or vice versa
    for (const pingId of activePingIds) {
      if (pongConnectionId.includes(pingId) || pingId.includes(pongConnectionId)) {
        console.log('🔍 PING: Found substring match', {
          pongId: pongConnectionId,
          pingId: pingId
        });
        return pingId;
      }
    }

    // Strategy 3: If we only have one active ping, use it (common case)
    if (activePingIds.length === 1) {
      console.log('🔍 PING: Using single active ping match', {
        pongId: pongConnectionId,
        pingId: activePingIds[0]
      });
      return activePingIds[0];
    }

    return null;
  }  /**
   * Handle ping timeout - no response received
   */
  private handlePingTimeout(connectionId: string): void {
    const pingData = this.activePings.get(connectionId);

    if (pingData) {
      this.activePings.delete(connectionId);
      this.consecutiveFailedPings++;

      const timeoutDuration = Date.now() - pingData.timestamp;

      console.warn('⚠️ PING: Timeout for connection', {
        connectionId,
        timeoutAfter: `${timeoutDuration}ms`,
        timestamp: new Date().toISOString(),
        activePings: this.activePings.size,
        consecutiveFailures: this.consecutiveFailedPings,
        totalActivePings: Array.from(this.activePings.keys())
      });

      // Update ping response rate
      this.updatePingResponseRate(false);

      // Check if we've hit the maximum consecutive failures
      if (this.consecutiveFailedPings >= this.MAX_CONSECUTIVE_PING_FAILURES) {
        console.error('❌ PING: Maximum consecutive ping failures reached', {
          consecutiveFailures: this.consecutiveFailedPings,
          maxAllowed: this.MAX_CONSECUTIVE_PING_FAILURES,
          recommendation: 'Triggering connection recovery'
        });

        console.error('🛑 EMERGENCY: STOPPING ping system due to max failures', {
          activeTimers: this.activePings.size,
          action: 'force-stop and reconnect'
        });

        // CIRCUIT BREAKER: Open circuit immediately on max failures
        this.openCircuitBreaker('max_consecutive_ping_failures');

        // EMERGENCY: Stop all ping activity immediately
        this.stopAllPingActivity();

        console.error('🛑 EMERGENCY: All ping activity stopped', {
          activePings: 0,
          activeTimers: 0
        });

        this.emitEvent('ping_max_failures_reached', {
          consecutiveFailures: this.consecutiveFailedPings,
          connectionId,
          timeoutDuration,
          activePings: this.activePings.size,
          pingResponseRate: this.pingResponseRate
        });

        // Reset counter to prevent multiple triggers
        this.consecutiveFailedPings = 0;

        // Force connection health check that may trigger reconnection
        this.emitEvent('connection_health_critical', {
          reason: 'max_consecutive_ping_failures',
          failureCount: this.MAX_CONSECUTIVE_PING_FAILURES,
          responseRate: this.pingResponseRate
        });

        return;
      }

      // Only emit regular timeout if response rate is concerning
      if (this.pingResponseRate < 70) {
        this.emitEvent('ping_timeout', {
          connectionId,
          timeoutDuration,
          activePings: this.activePings.size,
          pingResponseRate: this.pingResponseRate,
          consecutiveFailures: this.consecutiveFailedPings
        });

        // Update connection health based on ping failures
        this.updateConnectionHealth(-5);

        // If ping response rate is too low, consider connection unhealthy
        if (this.pingResponseRate < 50) {
          console.warn('⚠️ PING: Low response rate detected', {
            rate: `${this.pingResponseRate.toFixed(1)}%`,
            consecutiveFailures: this.consecutiveFailedPings,
            recommendation: 'Consider connection health check'
          });

          this.emitEvent('ping_health_warning', {
            responseRate: this.pingResponseRate,
            activePings: this.activePings.size,
            consecutiveFailures: this.consecutiveFailedPings
          });
        }
      } else {
        // Don't treat occasional timeouts as severe if overall rate is good
        console.log('ℹ️ PING: Timeout occurred but overall response rate is healthy', {
          rate: `${this.pingResponseRate.toFixed(1)}%`,
          consecutiveFailures: this.consecutiveFailedPings,
          connectionId
        });
      }
    }
  }

  /**
   * Update ping response rate based on success/failure
   */
  private updatePingResponseRate(success: boolean): void {
    // Use exponential moving average to track response rate
    const alpha = 0.1; // Smoothing factor
    const newValue = success ? 100 : 0;

    this.pingResponseRate = alpha * newValue + (1 - alpha) * this.pingResponseRate;

    // Ensure rate stays within bounds
    this.pingResponseRate = Math.max(0, Math.min(100, this.pingResponseRate));

    // EMERGENCY FIX: Critical response rate triggers
    if (this.pingResponseRate < 60 && this.consecutiveFailedPings >= 2) {
      console.error('🚨 EMERGENCY: Response rate critically low', {
        rate: `${this.pingResponseRate.toFixed(1)}%`,
        consecutiveFailures: this.consecutiveFailedPings,
        recommendation: 'Immediate reconnection required'
      });

      this.emitEvent('ping_emergency_low_rate', {
        responseRate: this.pingResponseRate,
        consecutiveFailures: this.consecutiveFailedPings,
        action: 'immediate_reconnection_required',
        timeSinceLastResponse: this.lastPingResponseTime ? Date.now() - this.lastPingResponseTime : 0
      });
    }

    // EMERGENCY OVERRIDE: Force action at 50% rate
    if (this.pingResponseRate < 50 && this.consecutiveFailedPings >= 1) {
      console.error('🚨 EMERGENCY OVERRIDE: Rate below 50%, taking direct action', {
        rate: `${this.pingResponseRate.toFixed(1)}%`,
        consecutiveFailures: this.consecutiveFailedPings,
        action: 'bypass_event_system'
      });

      // Add custom log to match user's log pattern
      console.log('PING: Response rate critically low', {
        rate: `${this.pingResponseRate.toFixed(1)}%`,
        timestamp: new Date().toISOString()
      });

      this.emitEvent('ping_emergency_override', {
        responseRate: this.pingResponseRate,
        consecutiveFailures: this.consecutiveFailedPings,
        action: 'force_reconnection_bypass',
        severity: 'critical'
      });
    }
  }

  /**
   * Clean up old ping tracking data
   */
  private cleanupOldPings(): void {
    const thirtySecondsAgo = Date.now() - 30000;  // Reduced from 45s for faster cleanup

    for (const [connectionId, pingData] of this.activePings.entries()) {
      if (pingData.timestamp < thirtySecondsAgo) {
        window.clearTimeout(pingData.timeout);
        this.activePings.delete(connectionId);

        console.warn('🧹 PING: Cleaned up stale ping tracking', {
          connectionId,
          age: Date.now() - pingData.timestamp
        });
      }
    }
  }  /**
   * Get ping statistics for monitoring
   */
  public getPingStats(): {
    activePings: number;
    responseRate: number;
    lastResponseTime: number;
    timeSinceLastResponse: number;
    consecutiveFailures: number;
    maxConsecutiveFailures: number;
  } {
    return {
      activePings: this.activePings.size,
      responseRate: this.pingResponseRate,
      lastResponseTime: this.lastPingResponseTime,
      timeSinceLastResponse: this.lastPingResponseTime ? Date.now() - this.lastPingResponseTime : 0,
      consecutiveFailures: this.consecutiveFailedPings,
      maxConsecutiveFailures: this.MAX_CONSECUTIVE_PING_FAILURES
    };
  }

  /**
   * EMERGENCY: Stop all ping activity immediately
   */
  private stopAllPingActivity(): void {
    console.error('🛑 EMERGENCY: Stopping all ping activity', {
      activePings: this.activePings.size,
      activeTimers: Array.from(this.activePings.keys())
    });

    // Clear all active ping timeouts
    this.activePings.forEach(pingData => {
      window.clearTimeout(pingData.timeout);
    });
    this.activePings.clear();

    // Clear connection verification timers
    this.clearConnectionVerificationTimers();

    console.log('✅ EMERGENCY: All ping activity stopped');
  }

  // ========================================
  // CIRCUIT BREAKER IMPLEMENTATION
  // ========================================

  /**
   * Check if we can execute operations (respects circuit breaker and cooldown)
   */
  private canExecuteOperation(): boolean {
    const now = Date.now();

    // Check emergency cooldown period
    if (this.emergencyStopActivated) {
      const cooldownRemaining = this.EMERGENCY_COOLDOWN_PERIOD - (now - this.emergencyStopTime);
      if (cooldownRemaining > 0) {
        console.log('🚫 CIRCUIT BREAKER: Emergency cooldown active', {
          remaining: `${cooldownRemaining}ms`,
          timestamp: new Date().toISOString()
        });
        return false;
      } else {
        // Cooldown period elapsed, reset emergency stop
        this.emergencyStopActivated = false;
        console.log('✅ CIRCUIT BREAKER: Emergency cooldown period elapsed, allowing operations');
      }
    }

    // Check circuit breaker state
    switch (this.circuitBreakerState) {
      case 'CLOSED':
        return true;

      case 'OPEN':
        const timeSinceOpen = now - this.circuitOpenTime;
        if (timeSinceOpen >= this.CIRCUIT_RECOVERY_TIMEOUT) {
          // Transition to HALF_OPEN
          this.circuitBreakerState = 'HALF_OPEN';
          console.log('🔄 CIRCUIT BREAKER: Transitioning to HALF_OPEN state');
          return true;
        }
        console.log('🚫 CIRCUIT BREAKER: Circuit is OPEN', {
          timeSinceOpen: `${timeSinceOpen}ms`,
          recoveryTimeout: `${this.CIRCUIT_RECOVERY_TIMEOUT}ms`,
          remaining: `${this.CIRCUIT_RECOVERY_TIMEOUT - timeSinceOpen}ms`
        });
        return false;

      case 'HALF_OPEN':
        // Allow limited operations in half-open state
        console.log('🔄 CIRCUIT BREAKER: Allowing operation in HALF_OPEN state');
        return true;

      default:
        return true;
    }
  }

  /**
   * Reset circuit breaker state when a new connection is established
   */
  private resetCircuitBreakerOnConnection(): void {
    const wasOpen = this.circuitBreakerState === 'OPEN';
    const wasEmergencyStop = this.emergencyStopActivated;

    console.log('🔧 CIRCUIT BREAKER: Resetting state for new connection:', {
      previousState: this.circuitBreakerState,
      wasEmergencyStop: wasEmergencyStop,
      timestamp: new Date().toISOString()
    });

    // Reset circuit breaker state
    this.circuitBreakerState = 'CLOSED';
    this.emergencyStopActivated = false;
    this.consecutiveFailedPings = 0;

    if (wasOpen || wasEmergencyStop) {
      console.log('✅ CIRCUIT BREAKER: State reset - operations now allowed');
      this.emitEvent('circuit_breaker_reset', {
        reason: 'new_connection_established',
        previousState: wasOpen ? 'OPEN' : 'CLOSED',
        wasEmergencyStop: wasEmergencyStop
      });
    }
  }

  /**
   * Open the circuit breaker
   */
  private openCircuitBreaker(reason: string): void {
    const now = Date.now();
    this.circuitBreakerState = 'OPEN';
    this.circuitOpenTime = now;
    this.emergencyStopActivated = true;
    this.emergencyStopTime = now;

    console.error('🚫 CIRCUIT BREAKER: Circuit OPENED', {
      reason,
      state: this.circuitBreakerState,
      emergencyStopActivated: this.emergencyStopActivated,
      cooldownPeriod: this.EMERGENCY_COOLDOWN_PERIOD,
      recoveryTimeout: this.CIRCUIT_RECOVERY_TIMEOUT,
      timestamp: new Date().toISOString()
    });

    this.emitEvent('circuit_breaker_opened', {
      reason,
      cooldownPeriod: this.EMERGENCY_COOLDOWN_PERIOD,
      recoveryTimeout: this.CIRCUIT_RECOVERY_TIMEOUT
    });
  }

  /**
   * Close the circuit breaker
   */
  private closeCircuitBreaker(): void {
    if (this.circuitBreakerState !== 'CLOSED') {
      console.log('✅ CIRCUIT BREAKER: Circuit CLOSED - operations allowed');
      this.circuitBreakerState = 'CLOSED';
      this.consecutiveFailedPings = 0; // Reset failure count

      this.emitEvent('circuit_breaker_closed', {
        state: this.circuitBreakerState
      });
    }
  }

  /**
   * Record circuit breaker success
   */
  private recordCircuitBreakerSuccess(): void {
    if (this.circuitBreakerState === 'HALF_OPEN') {
      // Success in half-open state closes the circuit
      this.closeCircuitBreaker();
    } else if (this.circuitBreakerState === 'CLOSED') {
      // Reset any failure counters on success
      this.consecutiveFailedPings = 0;
    }
  }

  /**
   * Record circuit breaker failure
   */
  private recordCircuitBreakerFailure(): void {
    const now = Date.now();

    if (this.circuitBreakerState === 'HALF_OPEN') {
      // Failure in half-open state reopens the circuit
      this.openCircuitBreaker('failure_in_half_open_state');
    } else if (this.circuitBreakerState === 'CLOSED') {
      this.consecutiveFailedPings++;

      if (this.consecutiveFailedPings >= this.MAX_CONSECUTIVE_PING_FAILURES) {
        this.openCircuitBreaker('consecutive_failure_threshold_exceeded');
      }
    }
  }

  /**
   * Get remaining cooldown time in milliseconds
   */
  private getRemainingCooldownTime(): number {
    if (!this.emergencyStopActivated) {
      return 0;
    }

    const elapsed = Date.now() - this.emergencyStopTime;
    return Math.max(0, this.EMERGENCY_COOLDOWN_PERIOD - elapsed);
  }

  /**
   * Get circuit breaker status for external monitoring
   */
  public getCircuitBreakerStatus(): {
    state: string;
    isOperational: boolean;
    cooldownRemaining: number;
    consecutiveFailures: number;
    emergencyStopActive: boolean;
  } {
    return {
      state: this.circuitBreakerState,
      isOperational: this.canExecuteOperation(),
      cooldownRemaining: this.getRemainingCooldownTime(),
      consecutiveFailures: this.consecutiveFailedPings,
      emergencyStopActive: this.emergencyStopActivated
    };
  }

  ngOnDestroy(): void {
    // MEMORY LEAK PREVENTION: Complete cleanup of all resources
    this.clearConnectionVerificationTimers();
    this.cleanupActivePings();
    this.forceDisconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cleanup all active ping tracking
   */
  private cleanupActivePings(): void {
    for (const [connectionId, pingData] of this.activePings.entries()) {
      window.clearTimeout(pingData.timeout);
    }
    this.activePings.clear();
  }
}
